import { InputNumber, Select, Space } from 'antd'
import useFormInstance from 'antd/es/form/hooks/useFormInstance'
import { useEffect, useState } from 'react'

const MINUTES_IN_HOUR = 60
const MINUTES_IN_DAY = 60 * 24
type TimeUnit = 'minute' | 'hour' | 'day'

/**
 * Converts a numerical time value to minutes based on the provided unit.
 *
 * @param value - The numeric time value to convert.
 * @param unit - The unit of the time value. Valid options are 'minute', 'hour', and 'day'.
 * @returns The time value converted into minutes.
 *
 * @remarks
 * - When the unit is 'minute', the original value is returned.
 * - When the unit is 'hour', the value is multiplied by 60.
 * - When the unit is 'day', the value is multiplied by 1440 (60 * 24).
 * - If the unit does not match any of the recognized options, the original value is returned.
 */
const getMinutesFromValueAndUnit = (value: number, unit: TimeUnit) => {
  switch (unit) {
    case 'minute':
      return value
    case 'hour':
      return value * MINUTES_IN_HOUR
    case 'day':
      return value * MINUTES_IN_DAY
    default:
      return value
  }
}

const getValueAndUnitFromMinutes: (minutes: number) => {
  value: number
  unit: TimeUnit
} = (minutes) => {
  if (minutes % MINUTES_IN_DAY === 0) {
    return { value: minutes / MINUTES_IN_DAY, unit: 'day' }
  } else if (minutes % MINUTES_IN_HOUR === 0) {
    return { value: minutes / MINUTES_IN_HOUR, unit: 'hour' }
  }
  return { value: minutes, unit: 'minute' }
}

interface TimeInputWithUnitProps {
  /** Must be identical to the Form.Item `name` */
  name: string
  /**
   * Current value of the input
   * If this value changes the input will be updated.
   */
  value?: number
  /**
   * Initial value of the input
   * This is used to set the initial value of the input when the component mounts.
   * If the value is not provided, it will be set to 0.
   * If this value changes the input will NOT be updated.
   */
  initialValue?: number
}

const TimeInputWithUnit: React.FC<TimeInputWithUnitProps> = ({
  name,
  value: propsValue = undefined,
  initialValue = 0,
}) => {
  const form = useFormInstance()
  const [unit, setUnit] = useState<TimeUnit>('minute')
  const [inputValue, setInputValue] = useState<number>(initialValue)

  const updateFormValue = (value: number, unit: TimeUnit) => {
    form.setFieldValue(name, getMinutesFromValueAndUnit(value, unit))
  }

  // Update the value when the user change the input
  const handleInputChange = (value: number | null) => {
    setInputValue(value || 0)
    updateFormValue(value || 0, unit)
  }

  // Update the unit when the user selects a new unit (minute, hour, day)
  const handleUnitChange = (unit: TimeUnit) => {
    setUnit(unit)
    updateFormValue(inputValue, unit)
  }

  useEffect(() => {
    if (!initialValue) {
      return
    }
    const { value, unit } = getValueAndUnitFromMinutes(initialValue)
    setInputValue(value)
    setUnit(unit)
  }, [name])

  useEffect(() => {
    if (!propsValue) {
      return
    }
    const { value, unit } = getValueAndUnitFromMinutes(propsValue)
    setInputValue(value)
    setUnit(unit)
  }, [propsValue])

  return (
    <Space.Compact className="w-full">
      <InputNumber
        value={inputValue}
        onChange={handleInputChange}
        style={{ flex: 1 }}
        min={0}
        step={1}
        precision={0}
      />
      <Select
        value={unit}
        onChange={handleUnitChange}
        options={[
          { value: 'minute', label: 'Minute' },
          { value: 'hour', label: 'Hour' },
          { value: 'day', label: 'Day' },
        ]}
        style={{ width: 'auto' }}
      />
    </Space.Compact>
  )
}

export default TimeInputWithUnit
